import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import PageLayout from "../../components/layouts/PageLayout";
import CustomNavbar from "../../components/common/CustomNavbar";
import { AppDispatch } from "../../store";
import { EmbeddedConfig, PhoneNumberOption, SignupState } from "../../types/EmbeddedSignupType";
import { useFacebookSignup } from "../../features/whatsapp-onboarding/useFacebookSignup";
import {
    connectEmbeddedSignup,
    disconnectEmbeddedSignup,
    fetchEmbeddedConfig,
    fetchEmbeddedStatus,
    retryEmbeddedSignup,
    selectEmbeddedNumber,
    sendEmbeddedTestMessage,
    submitEmbeddedPin,
} from "../../features/whatsapp-onboarding/EmbeddedSignupServices";
import Checklist, { ChecklistItem } from "./Checklist";
import Stepper from "./Stepper";
import { MOCK_STATES } from "./mockData";

const PREVIEW = false; // true = no API calls, use mock data and the screen switcher

const GREEN = "bg-[#0f7b5a] hover:bg-[#0c6a4d] text-white";
const btnPrimary = `${GREEN} rounded-lg px-5 py-2.5 text-sm font-semibold transition disabled:opacity-50`;
const btnGhost =
    "rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-[#10231d] transition hover:bg-gray-50 disabled:opacity-50";

const BADGES: Record<string, { label: string; cls: string }> = {
    NOT_CONNECTED: { label: "Not connected", cls: "bg-gray-100 text-gray-700" },
    IN_PROGRESS: { label: "Setting up", cls: "bg-[#fdf1dc] text-amber-800" },
    CHOOSE_NUMBER: { label: "Choose a number", cls: "bg-[#fdf1dc] text-amber-800" },
    PIN_NEEDED: { label: "PIN needed", cls: "bg-[#fdf1dc] text-amber-800" },
    NEEDS_ATTENTION: { label: "Needs attention", cls: "bg-red-100 text-red-700" },
    CONNECTED: { label: "Live", cls: "bg-emerald-100 text-emerald-800" },
};

const SUBTITLES: Record<string, string> = {
    NOT_CONNECTED: "Click Connect WhatsApp and log in with the Facebook account that manages your business.",
    IN_PROGRESS: "Setting things up. This usually takes a few seconds.",
    CHOOSE_NUMBER: "Choose which phone number to use.",
    PIN_NEEDED: "Enter your number's 6-digit two-step verification PIN.",
    NEEDS_ATTENTION: "Fix the problem below and click Retry.",
};

const LIMITS: Record<string, string> = {
    TIER_250: "250 customers a day",
    TIER_1K: "1,000 customers a day",
    TIER_10K: "10,000 customers a day",
    TIER_100K: "100,000 customers a day",
    TIER_UNLIMITED: "Unlimited customers a day",
};

// preview mode: which mock screen each action leads to
const PREVIEW_NEXT: Record<string, string> = {
    NOT_CONNECTED: "CHOOSE_NUMBER",
    CHOOSE_NUMBER: "PIN_NEEDED",
    PIN_NEEDED: "CONNECTED",
    NEEDS_ATTENTION: "CONNECTED",
    IN_PROGRESS: "CONNECTED",
};

const errText = (e: SignupState["lastError"]) => (typeof e === "string" ? e : e?.message);
const numId = (n: PhoneNumberOption) => (n.id || n.phoneNumberId || "") as string;
const numLabel = (n: PhoneNumberOption) => n.displayPhoneNumber || numId(n);
const numName = (n: PhoneNumberOption) => n.verifiedName || "";

function Row({ label, value, dot }: { label: string; value: string; dot?: string }) {
    return (
        <div>
            <div className="text-xs text-gray-500">{label}</div>
            <div className="mt-0.5 flex items-center gap-2 text-sm font-semibold text-[#10231d]">
                {dot && <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />}
                {value}
            </div>
        </div>
    );
}

export default function ConnectWhatsApp() {
    const dispatch = useDispatch<AppDispatch>();

    const [config, setConfig] = useState<EmbeddedConfig | null>(null);
    const [state, setState] = useState<SignupState | null>(null);
    const [booting, setBooting] = useState(true);
    const [busy, setBusy] = useState(false);

    const [selectedNumber, setSelectedNumber] = useState("");
    const [pin, setPin] = useState("");
    const [testNumber, setTestNumber] = useState("");
    const [testNote, setTestNote] = useState("");

    const { sdkReady, launch } = useFacebookSignup(config);

    // ---------- load ----------
    useEffect(() => {
        if (PREVIEW) {
            setState(MOCK_STATES.NOT_CONNECTED);
            setBooting(false);
            return;
        }
        (async () => {
            try {
                const [cfg, st] = await Promise.all([
                    dispatch(fetchEmbeddedConfig()).unwrap(),
                    dispatch(fetchEmbeddedStatus()).unwrap(),
                ]);
                setConfig(cfg);
                setState(st);
            } catch (e: any) {
                toast.error(e || "Could not load WhatsApp setup");
            } finally {
                setBooting(false);
            }
        })();
    }, [dispatch]);

    // shared runner: busy state, stores the new state, surfaces errors
    const run = useCallback(
        async (action: () => Promise<SignupState | null>, onOk?: (s: SignupState | null) => void) => {
            if (PREVIEW) {
                const target = PREVIEW_NEXT[state?.status ?? "NOT_CONNECTED"];
                const nextState = target ? MOCK_STATES[target] : state;
                if (nextState) setState(nextState);
                onOk?.(nextState ?? null);
                return;
            }
            setBusy(true);
            try {
                const next = await action();
                if (next) setState(next);
                onOk?.(next);
            } catch (e: any) {
                toast.error(typeof e === "string" ? e : "Something went wrong. Please try again.");
            } finally {
                setBusy(false);
            }
        },
        [state]
    );

    // ---------- actions ----------
    const handleConnect = () =>
        run(async () => {
            const r = await launch().catch((e: Error) => {
                throw e.message;
            });
            return dispatch(connectEmbeddedSignup(r)).unwrap();
        });

    const handleUseNumber = () =>
        run(() => dispatch(selectEmbeddedNumber({ phoneNumberId: selectedNumber })).unwrap());

    const handlePin = () =>
        run(async () => {
            const s = await dispatch(submitEmbeddedPin({ pin })).unwrap();
            setPin("");
            return s;
        });

    const handleRetry = () => run(() => dispatch(retryEmbeddedSignup()).unwrap());

    const handleRefresh = () => run(() => dispatch(fetchEmbeddedStatus()).unwrap());

    const handleTest = () =>
        run(
            () => dispatch(sendEmbeddedTestMessage({ mobileNumber: testNumber.replace(/\D/g, "") })).unwrap(),
            () => setTestNote("Sent. Check WhatsApp on that phone for Meta's hello_world message.")
        );

    const handleDisconnect = () => {
        if (!window.confirm("Disconnect this number? Billing and messaging will stop.")) return;
        if (PREVIEW) {
            setState(MOCK_STATES.NOT_CONNECTED);
            setTestNote("");
            return;
        }
        run(
            async () => {
                await dispatch(disconnectEmbeddedSignup()).unwrap();
                return dispatch(fetchEmbeddedStatus()).unwrap();
            },
            () => {
                setTestNote("");
                toast.success("Number disconnected");
            }
        );
    };

    // ---------- derived ----------
    const status = state?.status ?? "NOT_CONNECTED";
    const failed = status === "NEEDS_ATTENTION";
    const error = errText(state?.lastError);
    const live = status === "CONNECTED";

    const currentStep = useMemo(() => {
        if (live) return 8;
        if (state?.step) return state.step;
        switch (status) {
            case "CHOOSE_NUMBER": return 3;
            case "PIN_NEEDED": return 6;
            case "IN_PROGRESS": return 4;
            case "NEEDS_ATTENTION": return 6;
            default: return 1;
        }
    }, [state, status, live]);

    const numbers = state?.phoneNumbers ?? [];
    useEffect(() => {
        if (status === "CHOOSE_NUMBER" && !selectedNumber && numbers.length) {
            setSelectedNumber(numId(numbers[0]));
        }
    }, [status, numbers, selectedNumber]);

    const displayName = state?.verifiedName || state?.wabaName || "";
    const verified = state?.businessVerification?.toLowerCase() === "verified";
    const billingOk = state?.billing === "EMPEDANCE";
    const billingPending = state?.billing === "EMPEDANCE_PENDING";
    const reviewApproved = state?.accountReviewStatus === "APPROVED";

    // fallback only used if the API doesn't send a checklist
    const fallbackChecklist: ChecklistItem[] = [
        {
            title: "Connect your WhatsApp number",
            hint: "Use a number that isn't on the WhatsApp app, or delete it from the app first.",
            done: live,
        },
        {
            title: "WhatsApp billing through Empedance",
            hint: "Empedance sets this up for you; message charges come from your wallet.",
            done: billingOk,
        },
        {
            title: "Display name approved by Meta",
            hint: "Checked after connecting.",
            done: state?.nameStatus === "APPROVED",
        },
        {
            title: "Verify your business on Meta",
            hint: "Until verified you can message 250 customers a day. Meta Business Settings, Security Center.",
            done: verified,
        },
        {
            title: "Send a test message",
            hint: "Sends Meta's hello_world template to your own number.",
            done: !!state?.testMessageSent,
        },
    ];

    const checklist: ChecklistItem[] = state?.checklist?.length
        ? state.checklist.map((c) => ({ title: c.label, hint: c.hint ?? undefined, done: c.done }))
        : fallbackChecklist;

    const quality = (state?.qualityRating || "").toLowerCase();
    const qualityLabel = quality ? quality[0].toUpperCase() + quality.slice(1) : "—";
    const qualityDot =
        quality === "green" ? "bg-emerald-600" : quality === "yellow" ? "bg-amber-500" : quality === "red" ? "bg-red-600" : "bg-gray-400";

    const badge = BADGES[status] ?? BADGES.NOT_CONNECTED;

    // ---------- right-hand panel ----------
    const panel = () => {
        if (live) {
            return (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                        <Row
                            label="Display name"
                            value={displayName || "—"}
                            dot={state?.nameStatus === "APPROVED" ? "bg-emerald-600" : "bg-amber-500"}
                        />
                        <Row label="Quality" value={qualityLabel} dot={qualityDot} />
                        <Row
                            label="Daily limit"
                            value={LIMITS[state?.messagingLimit || ""] || state?.messagingLimit || "—"}
                        />
                        <Row
                            label="Business verification"
                            value={verified ? "Verified" : "Not verified"}
                            dot={verified ? "bg-emerald-600" : "bg-amber-500"}
                        />
                        <Row
                            label="Account review"
                            value={reviewApproved ? "Approved" : state?.accountReviewStatus || "Pending"}
                            dot={reviewApproved ? "bg-emerald-600" : "bg-amber-500"}
                        />
                        <Row
                            label="WhatsApp charges"
                            value={
                                billingOk
                                    ? "Billed by Empedance from your wallet"
                                    : billingPending
                                    ? "Being set up by Empedance"
                                    : "—"
                            }
                            dot={billingOk ? "bg-emerald-600" : "bg-amber-500"}
                        />
                    </div>

                    <div className="border-t border-gray-200 pt-5">
                        <div className="mb-2 text-sm font-semibold text-[#10231d]">Send a test message</div>
                        <div className="flex gap-2">
                            <input
                                value={testNumber}
                                onChange={(e) => setTestNumber(e.target.value)}
                                placeholder="919876543210"
                                inputMode="numeric"
                                className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0f7b5a]"
                            />
                            <button
                                className={btnPrimary}
                                disabled={busy || testNumber.replace(/\D/g, "").length < 8}
                                onClick={handleTest}
                            >
                                {state?.testMessageSent ? "Send again" : "Send test"}
                            </button>
                        </div>
                        {testNote && <p className="mt-2 text-sm text-[#0f7b5a]">{testNote}</p>}
                    </div>

                    <div className="flex flex-wrap gap-3 border-t border-gray-200 pt-5">
                        <button className={btnGhost} disabled={busy} onClick={handleRefresh}>
                            Refresh details
                        </button>
                        {/* <button
                            className="hrounded-lg border border-red-200 bg-white px-5 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50"
                            disabled={busy}
                            onClick={handleDisconnect}
                        >
                            Disconnect number
                        </button> */}
                    </div>
                </div>
            );
        }

        if (status === "CHOOSE_NUMBER") {
            return (
                <div>
                    <h3 className="mb-4 text-lg font-semibold text-[#10231d]">Which number should customers see?</h3>
                    <div className="space-y-3">
                        {numbers.map((n) => {
                            const id = numId(n);
                            const on = id === selectedNumber;
                            return (
                                <label
                                    key={id}
                                    className={`flex cursor-pointer items-center gap-3 rounded-lg border bg-white px-4 py-3 ${
                                        on ? "border-2 border-[#0f7b5a]" : "border-gray-200"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="phone"
                                        checked={on}
                                        onChange={() => setSelectedNumber(id)}
                                        className="accent-[#0f7b5a]"
                                    />
                                    <span>
                                        <span className="block text-sm font-semibold text-[#10231d]">{numLabel(n)}</span>
                                        <span className="block text-xs text-gray-500">{numName(n)}</span>
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                    <button className={`${btnPrimary} mt-5`} disabled={busy || !selectedNumber} onClick={handleUseNumber}>
                        {busy ? "Saving…" : "Use this number"}
                    </button>
                </div>
            );
        }

        if (status === "PIN_NEEDED") {
            return (
                <div>
                    <h3 className="mb-2 text-lg font-semibold text-[#10231d]">Enter your two-step PIN</h3>
                    <p className="mb-4 text-sm text-gray-600">
                        This number already has a 6-digit PIN. Forgot it? Reset it in WhatsApp Manager under Phone numbers.
                    </p>
                    <input
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        onKeyDown={(e) => e.key === "Enter" && pin.length === 6 && handlePin()}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="6-digit PIN"
                        className="w-48 rounded-lg border border-gray-300 bg-white px-4 py-3 text-center text-lg tracking-[0.35em] outline-none focus:border-[#0f7b5a]"
                    />
                    <div>
                        <button className={`${btnPrimary} mt-5`} disabled={busy || pin.length !== 6} onClick={handlePin}>
                            {busy ? "Checking…" : "Continue"}
                        </button>
                    </div>
                </div>
            );
        }

        // NOT_CONNECTED, IN_PROGRESS, NEEDS_ATTENTION
        return (
            <div>
                <h3 className="mb-3 text-lg font-semibold text-[#10231d]">Before you start</h3>
                <ul className="mb-5 list-disc space-y-2 pl-5 text-sm text-gray-700">
                    <li>Log in with the Facebook account that manages your business.</li>
                    <li>Pick or create your WhatsApp Business account and number in the popup.</li>
                    <li>The number must not be on the WhatsApp app. Delete it from the app first if it is.</li>
                </ul>
                <div className="flex flex-wrap gap-3">
                    {(status === "NOT_CONNECTED" || failed) && (
                        <button
                            className={btnPrimary}
                            disabled={busy || (!PREVIEW && !sdkReady)}
                            onClick={handleConnect}
                        >
                            {busy ? "Connecting…" : "Connect WhatsApp"}
                        </button>
                    )}
                    {failed && (
                        <button className={btnGhost} disabled={busy} onClick={handleRetry}>
                            Retry the failed step
                        </button>
                    )}
                    {status === "IN_PROGRESS" && (
                        <button className={btnGhost} disabled={busy} onClick={handleRefresh}>
                            Refresh status
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <PageLayout
            customNavbar={
                <CustomNavbar>
                    <span className="text-lg font-bold uppercase">WhatsApp setup</span>
                </CustomNavbar>
            }
        >
            <div className="mx-auto max-w-6xl p-4 md:p-8">
                {PREVIEW && (
                    <select
                        className="mb-4 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                        value={state?.status}
                        onChange={(e) => setState(MOCK_STATES[e.target.value])}
                    >
                        {Object.keys(MOCK_STATES).map((k) => (
                            <option key={k} value={k}>
                                {k}
                            </option>
                        ))}
                    </select>
                )}

                <div className="rounded-2xl bg-white p-6 shadow-lg md:p-10">
                    {booting ? (
                        <p className="py-20 text-center text-sm text-gray-500">Loading…</p>
                    ) : (
                        <>
                            <header className="flex items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-3xl font-extrabold leading-tight text-[#10231d] md:text-4xl">
                                        {live ? state?.displayPhoneNumber || "Connected" : "Connect your WhatsApp number"}
                                    </h2>
                                    <p className="mt-2 max-w-lg text-base text-gray-600">
                                        {live
                                            ? `${displayName || "Your business"} is live on WhatsApp.`
                                            : SUBTITLES[status]}
                                    </p>
                                    {live && (
                                        <p className="mt-3 text-sm font-medium text-[#0f7b5a]">
                                            {state?.nextAction ||
                                                "You're ready. Recharge your wallet, create a template or start a broadcast."}
                                        </p>
                                    )}
                                </div>
                                <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${badge.cls}`}>
                                    {badge.label}
                                </span>
                            </header>

                            {error && (status === "PIN_NEEDED" || failed) && (
                                <div className="mt-5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
                            )}

                            <div className="mt-8 grid gap-10 md:grid-cols-[260px_1fr]">
                                <Stepper
                                    current={currentStep}
                                    failed={failed}
                                    details={{
                                        2: state?.wabaName,
                                        3: state?.displayPhoneNumber,
                                    }}
                                />
                                <div className="self-start rounded-xl bg-[#f3f6f4] p-6">{panel()}</div>
                            </div>

                            <Checklist items={checklist} />
                        </>
                    )}
                </div>
            </div>
        </PageLayout>
    );
}